import InlineAlert from "@storeblocks/inline-alert";
import { H4, StyledRow } from "App/global-style";
import {
  ContentWrapper,
  StyledCheckbox,
} from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/styled";
import { Price } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/AdvicePrice/InsurancePrice";
import { filterCoveragesOnCoverage } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/houseProductsUtils";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { DittoCoverageCardDetails } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/DittoCoverages/DittoCoverageCardDetails";
import { Description } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.styles";
import { Checkbox } from "Components/Form/Checkbox";
import { RadioButtonTrueFalse } from "Components/Form/RadioButton";
import { PriceWrapper } from "Components/InsuranceDetailsCard";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { PRESENTATION } from "Components/NumberValue/styled";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { ChildHealthCoverage } from "Store/interfaces/products/coverage";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { useTexts } from "Store/texts";
import { getError } from "Utils/productUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import { FormikProps } from "formik";
import React from "react";
import ReactMarkdown from "react-markdown";
import { ChildPriceFormValues } from "../childProductPriceInterfaces";
import { ChildHealthContainer, ConsentWrapper } from "./childHealth.styled";

interface Props {
  formProps: FormikProps<ChildPriceFormValues>;
  priceIsLoading: boolean;
  childName?: string;
}

export const ChildHealthElements = ({
  formProps,
  priceIsLoading,
  childName,
}: Props): React.ReactElement | null => {
  const texts = useTexts();
  const childHealthEnabled = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_2325_CHILD_HEALTH,
  );
  const coverages = useCoveragePriceCalculations();
  const childHealthCoverage = filterCoveragesOnCoverage(
    Object.values(ChildHealthCoverage),
    coverages,
  )[0];

  if (
    formProps.values.coverage === undefined ||
    formProps.values.relatedChildHealthProductId ||
    !childHealthEnabled
  ) {
    return null;
  }

  return (
    <ChildHealthContainer>
      <H4>
        {`${texts.domain["childHealth.checkPrice.addChildHealth.header"]} `}
        <span className="data-hj-suppress">{childName}</span>
        {`?`}
      </H4>
      <DetailsCardWrapper
        key={"Child Health"}
        selected={formProps.values.buyChildHealthInsurance}
        dynamicInsuranceDetailsCard
        additionalCoverage
      >
        <StyledRow>
          <StyledCheckbox className="InsuranceCheckbox">
            <Checkbox
              id={"buyChildHealthInsurance"}
              name={"buyChildHealthInsurance"}
              label={"Helseforsikring Topp fra ERGO"}
              onChange={(e) => {
                formProps.setFieldValue(
                  "buyChildHealthInsurance",
                  e.target.checked,
                );
                if (e.target.checked) {
                  formProps.setFieldValue(
                    "childHealthCoverage",
                    ChildHealthCoverage.CHILD_HEALTH_TREATMENT,
                  );
                  formProps.setFieldTouched("ergoConsent", true);
                }
              }}
            />
          </StyledCheckbox>
          <PriceWrapper>
            <Price
              price={childHealthCoverage[1].prices[0].monthlyPrice}
              isChanging={false}
              loading={priceIsLoading}
              presentation={PRESENTATION.ADDITION}
            />
          </PriceWrapper>
        </StyledRow>
        <ContentWrapper>
          <Description fw={400} size={"14px"}>
            <ReactMarkdown>
              {
                texts.domain[
                  "childHealth.checkPrice.addChildHealth.description"
                ]
              }
            </ReactMarkdown>
          </Description>
          <DittoCoverageCardDetails
            product={ProductBackendName.CHILD_HEALTH}
            cardIsSelected={formProps.values.buyChildHealthInsurance}
            coverage={ChildHealthCoverage.CHILD_HEALTH_TREATMENT}
          />
          {formProps.values.buyChildHealthInsurance && (
            <ConsentWrapper>
              <RadioButtonTrueFalse
                name="ergoConsent"
                id="ergoConsent"
                label={texts.domain["childHealth.checkPrice.ergoConsent.label"]}
                hint={texts.domain["childHealth.checkPrice.ergoConsent.hint"]}
                options={[
                  {
                    id: "consentYes",
                    value: true,
                    label:
                      texts.domain["childHealth.checkPrice.ergoConsent.yes"],
                  },
                  {
                    id: "consentNo",
                    value: false,
                    label:
                      texts.domain["childHealth.checkPrice.ergoConsent.no"],
                  },
                ]}
                direction="vertical"
                error={getError("ergoConsent", formProps)}
              />
            </ConsentWrapper>
          )}
        </ContentWrapper>
      </DetailsCardWrapper>
      {formProps.values.ergoConsent === BooleanFormType.FALSE && (
        <InlineAlert
          variant="error"
          title={texts.domain["childHealth.checkPrice.ergoConsent.alert"]}
        />
      )}
    </ChildHealthContainer>
  );
};
